import React from 'react'

import Immutable from 'immutable'
import moment from 'moment'

import { getDeliveryDayOrder } from 'utils/deliverySlotHelper'
import type { UserOrder } from 'utils/types/order'

import type { Date } from '../types'
import { Day } from './Day'
import { Title } from './Title'
import { getCalendarDates } from './utils/getCalendarDates'

import css from './Calendar.css'

const dayNoToDayName = (dayNo: number) => moment(dayNo, 'E').format('ddd')

function dateToDay(
  dateString: string,
  date: Date | undefined,
  weekNo: number,
  dayNo: number,
  selected: string,
  userOrders: Immutable.List<UserOrder>,
) {
  const { icon, orderId } = getDeliveryDayOrder(dateString, userOrders)

  return {
    weekNo: `${weekNo}`,
    dayNo: `${dayNo}`,
    date: dateString || null,
    disabled: date ? date.disabled : true,
    selected: date ? date.date === selected : false,
    icon: date ? date.icon : icon,
    orderId: date ? date.orderId : orderId,
  }
}

const createGrid = (
  start: string,
  finish: string,
  dates: Date[],
  selectedDate: string,
  userOrders: Immutable.List<UserOrder>,
) => {
  const startMoment = moment(start)
  const finishMoment = moment(finish)

  // days (calendar rows) by weekday
  const output: { [key: number]: ReturnType<typeof dateToDay>[] } = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
  }

  for (const m = startMoment; m.diff(finishMoment, 'days') <= 0; m.add(1, 'days')) {
    const dateString = m.format('YYYY-MM-DD')
    const matchingDate = dates.find((d) => d && d.date === dateString)
    const weekNo = parseInt(m.format('GGGGWW'), 10)
    const dayNo = parseInt(m.format('E'), 10)
    const weekday = m.isoWeekday()

    const day = dateToDay(dateString, matchingDate, weekNo, dayNo, selectedDate, userOrders)

    output[weekday].push(day)
  }

  return output
}

type CalendarProps = {
  dates: Date[]
  selected: string
  onClick: (date: string, orderId: string) => void
  userOrders?: Immutable.List<UserOrder>
}

export const Calendar = ({
  dates,
  selected,
  onClick,
  userOrders = Immutable.List(),
}: CalendarProps) => {
  if (!dates || dates.length === 0) {
    return null
  }

  const DAY_ORDER = [6, 7, 1, 2, 3, 4, 5]
  const { start, finish } = getCalendarDates(dates)
  const grid = createGrid(start, finish, dates, selected, userOrders)

  return (
    <div className={css.calendar} data-testid="calendar">
      <Title dates={dates} />
      <div className={css.grid}>
        {DAY_ORDER.map((dayNo) => (
          <div key={dayNo} className={css.column}>
            <div className={css.dayName}>{dayNoToDayName(dayNo)}</div>
            {grid[dayNo].map((date) => (
              <Day
                key={`${date.weekNo}-${date.dayNo}`}
                className={css.day}
                onClick={onClick}
                date={date.date}
                weekNo={date.weekNo}
                dayNo={date.dayNo}
                selected={date.selected}
                disabled={date.disabled}
                icon={date.icon}
                orderId={date.orderId}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
